<template>
    <div class="unlayer-container">
        <div v-if="!unlayerError" id="unlayer-editor" class="unlayer-editor" />
    </div>
</template>

<script>
import axios from 'axios';
import sentry from '@/analytics/sentry';

import { loadScript } from './unlayer.util';
import { EDITOR_VERSION } from '@/components/unlayer/constants/unlayer.constants';

const IMAGES_PAGE_SIZE = 25;

const DESIGN_UPDATED_EVENT = 'design:updated';
const DESIGN_LOADED_EVENT = 'design:loaded';
const EDITOR_READY_EVENT = 'editor:ready';

const MESSAGES = {
    BLOCK_DELETE_ERROR: 'For some reason your block didn’t get deleted. Try deleting it again.',
    BLOCK_LOAD_ERROR: 'For some reason your blocks didn’t load. Try again after refreshing the page.',
    BLOCK_SAVE_ERROR: 'For some reason your block didn’t save. Try saving again.',
    CLOSE_LABEL: 'Ok, got it',
    UNLAYER_ERROR: 'There was a problem opening the editor. Try again.',
    UPDATE_ERROR: 'For some reason the changes to your block didn’t update. Try making them again.',
    UPLOAD_ERROR: 'There was a problem trying to upload your image. Please try again.',
};

export default {
    name: 'UnlayerEditor',

    inject: {
        editorProvider: {
            default: {
                register() {},
                unregister() {},
                previewHtml: null,
            },
        },
    },

    props: {
        displayMode: {
            type: String,
            required: true,
            options: ['email', 'web'],
        },

        design: Object,

        options: {
            type: Object,
            default: () => ({}),
        },

        // Expose Unlayer's user id config option in order to enable user-generated
        // content blocks and uploaded images.
        unlayerUserId: String,
        unlayerProjectId: String,
        unlayerAuditToolEnabled: Boolean,
        unlayerBuilderVersionOverride: String,
        useUnlayerBuilderVersionOverrideEnabled: Boolean,
    },

    data() {
        return {
            blocks: [],
            unlayerError: false,
            userImagesPageToken: null,
            hasEmittedInitialLoad: false,
        };
    },

    async mounted() {
        try {
            await loadScript();
            this.initializeUnlayer();
        } catch (e) {
            this.handleUnlayerError();
        }
    },

    beforeDestroy() {
        if (!this.editor) {
            return;
        }

        this.$emit('unload', this.editor);
        this.editorProvider.unregister(this.editor);

        this.editor.removeEventListener(DESIGN_LOADED_EVENT, this.handleDesignLoaded);
        this.editor.removeEventListener(DESIGN_UPDATED_EVENT, this.handleDesignUpdate);
        this.editor.removeEventListener('onImageUpload', this.handleImageUpload);
        this.editor.removeEventListener(EDITOR_READY_EVENT, this.handleEditorReady);

        if (this.editorProvider.previewHtml) {
            this.editor.removeEventListener('previewHtml', this.editorProvider.previewHtml);
        }
    },

    methods: {
        initializeUnlayer() {
            const { displayMode, options, unlayerUserId } = this;

            const version = this.useUnlayerBuilderVersionOverrideEnabled && this.unlayerBuilderVersionOverride ? this.unlayerBuilderVersionOverride : EDITOR_VERSION;

            this.editor = window.unlayer.createEditor({
                displayMode,
                id: 'unlayer-editor',
                projectId: parseInt(this.unlayerProjectId, 10),
                version,
                ...(unlayerUserId && { user: { id: unlayerUserId } }),
                ...options,
            });

            this.editor.addEventListener(EDITOR_READY_EVENT, this.handleEditorReady);

            this.editorProvider.register(this.editor);

            if (this.editorProvider.previewHtml) {
                this.editor.addEventListener('previewHtml', this.editorProvider.previewHtml);
            }

            this.initializeBlockProvider();
            this.initializeImagesProvider();

            if (this.unlayerAuditToolEnabled) {
                this.registerCustomValidators();
            }
        },

        loadDesign() {
            this.preProcessDesign();

            this.editor.loadDesign(this.design);
        },

        preProcessDesign() {
            if (!this.design?.body) return;

            const { rows } = this.design.body;

            rows.forEach((row) => {
                const { columns } = row;

                columns.forEach(({ contents }) => {
                    if (contents.length === 0) return;

                    const content = contents[0];

                    if (content.type !== 'custom' && content.slug !== 'canSpamFooter') return;

                    row.values.backgroundColor = '#FFFFFF';
                });
            });
        },

        initializeBlockProvider() {
            this.editor.registerProvider('blocks', this.fetchBlocks);
            this.editor.registerCallback('block:added', this.handleBlockAdded);
            this.editor.registerCallback('block:removed', this.handleBlockDeleted);
            this.editor.registerCallback('block:modified', this.handleBlockUpdated);
        },

        initializeImagesProvider() {
            this.editor.registerProvider('userUploads', this.fetchImages);
            this.editor.registerCallback('image', this.handleImageUpload);
            this.editor.registerCallback('image:removed', this.handleImageDeleted);
        },

        registerCustomValidators() {
            const blocks = [
                'text',
                'heading',
                'image',
                'button',
                'html',
                'menu',
                'social',
                'timer',
                'video',
            ];

            blocks.forEach((block) => {
                // The callback function is converted to string and eval'ed on unlayer's
                // iframe and arrow function causes a problem when converting to string
                // eslint-disable-next-line prefer-arrow-callback
                this.editor.setToolValidator(block, function (data) {
                    // eslint-disable-next-line no-underscore-dangle
                    const { keapCustomErrors = [] } = data.values._meta;

                    return data.defaultErrors.concat(keapCustomErrors.map((error) => ({
                        ...error,
                        icon: 'fa-triangle-exclamation',
                        title: `${error.id}.title`,
                        description: `${error.id}.description`,
                        dismissable: true,
                    })));
                });
            });
        },

        async fetchBlocks (params, done) {
            const { BLOCK_LOAD_ERROR } = MESSAGES;

            try {
                const { data } = await axios.get('/app/unlayerEmailFunnel/blocks');

                this.blocks = data.map(({ id, json }) => ({ ...JSON.parse(json), id }));
            } catch (error) {
                this.displayError(BLOCK_LOAD_ERROR);
            }
            done(this.blocks);
        },

        handleDesignLoaded() {
            if (this.hasEmittedInitialLoad) {
                return;
            }

            this.hasEmittedInitialLoad = true;
            this.$emit('load', this.editor);
        },

        handleEditorReady() {
            this.editor.addEventListener(DESIGN_LOADED_EVENT, this.handleDesignLoaded);
            this.editor.addEventListener(DESIGN_UPDATED_EVENT, this.handleDesignUpdate);

            this.loadDesign();
        },

        async handleBlockAdded(block, done) {
            const { BLOCK_SAVE_ERROR } = MESSAGES;

            try {
                const { data: { id, json } } = await axios.post('/app/unlayerEmailFunnel/blocks', {
                    json: JSON.stringify(block),
                    templateSource: 'UNLAYER',
                });

                done({ ...JSON.parse(json), id });
            } catch (error) {
                sentry.captureException(error);
                this.displayError(BLOCK_SAVE_ERROR);
                done();
            }
        },

        async handleBlockDeleted(block, done) {
            const { BLOCK_DELETE_ERROR } = MESSAGES;

            try {
                await axios.delete(`/app/unlayerEmailFunnel/blocks/${block.id}`);
            } catch (error) {
                sentry.captureException(error);
                this.displayError(BLOCK_DELETE_ERROR);
            }

            done(block);
        },

        async handleBlockUpdated(block, done) {
            const { UPDATE_ERROR } = MESSAGES;
            let foundBlock = this.blocks.find(({ id }) => id === block.id);

            if (foundBlock == null) {
                this.displayError(UPDATE_ERROR);

                done();

                return;
            }

            foundBlock = {
                ...foundBlock,
                category: block.category,
                tags: block.tags,
            };

            try {
                await axios.put(`/app/unlayerEmailFunnel/blocks/${foundBlock.id}`, {
                    json: JSON.stringify(foundBlock),
                });

                done(foundBlock);
            } catch (error) {
                this.displayError(UPDATE_ERROR);
                done();
            }
        },

        displayError(message) {
            const { CLOSE_LABEL } = MESSAGES;

            this.$error({
                bottom: true,
                closeLabel: CLOSE_LABEL,
                message,
                showIcon: true,
            });
        },

        handleUnlayerError() {
            const { UNLAYER_ERROR } = MESSAGES;

            this.unlayerError = true;

            this.displayError(UNLAYER_ERROR);

            this.$emit('error');
        },

        handleDesignUpdate(change = {}) {
            // adding { cleanup: false } is a work around suggested by the unlayer team for a bug on unlayer's side that incorrectly removes html attributes. https://jira.infusionsoft.com/browse/EO-3907
            this.editor.exportHtml((data) => this.$emit('design-updated', { data, change }), { cleanup: false });
        },

        async fetchImages(params, done) {
            try {
                const { data } = await axios.get('/app/fileBox/images', {
                    params: {
                        pageKey: params.page > 1 ? this.userImagesPageToken : null,
                        pageSize: IMAGES_PAGE_SIZE,
                        searchString: params.searchText,
                    },
                });

                const images = data?.images.map((image) => ({
                    ...image,
                    source: 'user',
                    id: parseInt(image.id, 10),
                }));

                this.userImagesPageToken = data.nextPageKey;

                done(images, {
                    hasMore: this.userImagesPageToken != null,
                    page: params.page,
                    perPage: IMAGES_PAGE_SIZE,
                    total: data.total,
                });
            } catch (error) {
                sentry.captureException(error);
            }
        },

        async handleImageUpload({ attachments }, done) {
            const [file] = attachments;
            const formData = new FormData();
            const { UPLOAD_ERROR } = MESSAGES;

            formData.append('file', file);

            try {
                const { data } = await axios.post('/app/fileBox/images/upload', formData);

                done({ progress: 100, url: data.location });
            } catch (e) {
                sentry.captureException(e);
                this.displayError(UPLOAD_ERROR);
            }

            // TODO: Sometimes filebox will delete duplicate images, need to figure out how to best
            // present that feedback to the user
        },

        async handleImageDeleted(image, done) {
            const { data: { deleteUserUploadedImage } } = await axios.delete(`/app/unlayerEmailFunnel/images/${image.id}`);

            if (deleteUserUploadedImage) {
                done();
            }

            // TODO: Need designs for sad path/error handling
        },
    },
};
</script>

<style lang="scss" scoped>
.unlayer-container {
    display: flex;
    height: 100%;
}

.unlayer-editor {
    flex: 1;
    display: flex;
}
</style>
